import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { savePresentationSettings } from './api';
import AdminColorsSection from './admin/AdminColorsSection';
import AdminConfirmDialog from './admin/AdminConfirmDialog';
import AdminFieldsSection from './admin/AdminFieldsSection';
import AdminFontsSection from './admin/AdminFontsSection';
import AdminHealthSection from './admin/AdminHealthSection';
import AdminLanguagesSection from './admin/AdminLanguagesSection';
import AdminPromptsSection from './admin/AdminPromptsSection';
import AdminSectionHeader from './admin/AdminSectionHeader';
import AdminSectionNav, { AdminNavItem } from './admin/AdminSectionNav';
import AdminTextTableSection from './admin/AdminTextTableSection';
import {
  AdminSectionId,
  applyIncomingSettings,
  applySection,
  cloneSettings,
  isSectionDirty,
  isSettingsDirty,
  patchForSection,
  resetSectionToFactory,
} from './admin/adminSettingsState';
import { CONTROL, PRES, SPACE, TYPE } from './chrome';
import { COPY, settingsSaveLabel } from './presentationCopy';
import { usePresentationSettings } from './PresentationSettingsContext';
import { PresentationSettingsState } from './presentationSettings';
import { focusVisibleSx, reducedMotionSx } from './ui/focus';

const SECTION_META: Record<AdminSectionId, {
  label: string;
  description: string;
  intro: string;
  consequence: string;
}> = {
  colors: {
    label: COPY.settingsColors,
    description: COPY.settingsColorsNav,
    intro: COPY.settingsColorsIntro,
    consequence: COPY.settingsColorsConsequence,
  },
  prompts: {
    label: COPY.settingsPrompts,
    description: COPY.settingsPromptsNav,
    intro: COPY.settingsPromptsIntro,
    consequence: COPY.settingsPromptsConsequence,
  },
  textTable: {
    label: COPY.settingsTextTable,
    description: COPY.settingsTextTableNav,
    intro: COPY.settingsTextTableIntro,
    consequence: COPY.settingsTextTableConsequence,
  },
  fonts: {
    label: COPY.settingsFonts,
    description: COPY.settingsFontsNav,
    intro: COPY.settingsFontsIntro,
    consequence: COPY.settingsFontsConsequence,
  },
  health: {
    label: COPY.settingsHealth,
    description: COPY.settingsHealthNav,
    intro: COPY.settingsHealthIntro,
    consequence: COPY.settingsHealthConsequence,
  },
  fields: {
    label: COPY.settingsFields,
    description: COPY.settingsFieldsNav,
    intro: COPY.settingsFieldsIntro,
    consequence: COPY.settingsFieldsConsequence,
  },
  languages: {
    label: COPY.settingsLanguages,
    description: COPY.settingsLanguagesNav,
    intro: COPY.settingsLanguagesIntro,
    consequence: COPY.settingsLanguagesConsequence,
  },
};

function seedSettings(settings: PresentationSettingsState, factory: PresentationSettingsState): PresentationSettingsState {
  const next = cloneSettings(settings);
  next.prompts = settings.prompts || factory.prompts;
  return next;
}

const PresentationsAdminPage: React.FC<{ onBack: () => void; onBackLabel?: string }> = ({ onBack, onBackLabel }) => {
  const { settings, factory, reload } = usePresentationSettings();
  const [draft, setDraft] = useState<PresentationSettingsState>(() => seedSettings(settings, factory));
  const [baseline, setBaseline] = useState<PresentationSettingsState>(() => seedSettings(settings, factory));
  const [section, setSection] = useState<AdminSectionId>('colors');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const baselineRef = useRef(baseline);
  const hydratedRef = useRef(false);

  useEffect(() => {
    const incoming = seedSettings(settings, factory);
    if (!hydratedRef.current) {
      hydratedRef.current = true;
      baselineRef.current = incoming;
      setDraft(incoming);
      setBaseline(incoming);
      return;
    }
    setDraft((current) => applyIncomingSettings(current, incoming, baselineRef.current));
    baselineRef.current = incoming;
    setBaseline(incoming);
  }, [settings, factory]);

  useEffect(() => {
    const dirty = isSettingsDirty(draft, baseline);
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [draft, baseline]);

  const dirtyIds = useMemo(() => (
    (Object.keys(SECTION_META) as AdminSectionId[]).filter((id) => isSectionDirty(draft, baseline, id))
  ), [draft, baseline]);
  const sectionDirty = dirtyIds.includes(section);
  const anyDirty = dirtyIds.length > 0;
  const meta = SECTION_META[section];

  const navItems: AdminNavItem[] = (Object.keys(SECTION_META) as AdminSectionId[]).map((id) => ({
    id,
    label: SECTION_META[id].label,
    description: SECTION_META[id].description,
    dirty: dirtyIds.includes(id),
  }));

  const requestBack = () => {
    if (anyDirty) {
      setLeaveOpen(true);
      return;
    }
    onBack();
  };

  const confirmLeave = () => {
    setLeaveOpen(false);
    onBack();
  };

  const confirmReset = () => {
    setDraft((current) => resetSectionToFactory(current, factory, section));
    setResetOpen(false);
    setError(null);
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const saved = await savePresentationSettings(patchForSection(draft, section));
      const next = seedSettings(saved.settings, factory);
      const mergedDraft = applySection(draft, next, section);
      const mergedBaseline = applySection(baselineRef.current, next, section);
      baselineRef.current = mergedBaseline;
      setDraft(mergedDraft);
      setBaseline(mergedBaseline);
      await reload();
    } catch (err: any) {
      setError(err?.response?.data?.message || COPY.settingsFailed);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: PRES.surfaceSunken,
      fontFamily: TYPE.ui,
      ...reducedMotionSx,
    }}
    >
      <Box sx={{
        minHeight: 64,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1,
        bgcolor: PRES.surface,
        borderBottom: `1px solid ${PRES.borderSubtle}`,
        flexShrink: 0,
      }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={requestBack}
          sx={{ textTransform: 'none', color: PRES.interactive, fontWeight: 650, ...focusVisibleSx }}
        >
          {onBackLabel || COPY.allPresentations}
        </Button>
        <Box sx={{ width: '1px', alignSelf: 'stretch', my: 1.5, bgcolor: PRES.borderStrong, flexShrink: 0 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h1" sx={{ color: PRES.textPrimary, fontFamily: TYPE.ui, ...TYPE.title }}>
            {COPY.settings}
          </Typography>
          <Typography sx={{ color: PRES.textSecondary, fontFamily: TYPE.ui, ...TYPE.caption, display: { xs: 'none', sm: 'block' } }}>
            {COPY.settingsHelp}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, flexDirection: { xs: 'column', md: 'row' } }}>
        <Box sx={{ px: { xs: 2, md: 0 }, pt: { xs: 2, md: 0 }, height: { md: '100%' } }}>
          <AdminSectionNav
            items={navItems}
            section={section}
            onSelect={(id) => { setSection(id); setError(null); }}
          />
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto', px: { xs: 2, md: 3 }, py: 3 }}>
          <Box sx={{ maxWidth: 960 }}>
            <AdminSectionHeader title={meta.label} intro={meta.intro} consequence={meta.consequence} />
            {section === 'colors' && <AdminColorsSection draft={draft} setDraft={setDraft} />}
            {section === 'prompts' && <AdminPromptsSection draft={draft} setDraft={setDraft} />}
            {section === 'textTable' && <AdminTextTableSection draft={draft} setDraft={setDraft} />}
            {section === 'fonts' && <AdminFontsSection draft={draft} setDraft={setDraft} />}
            {section === 'health' && <AdminHealthSection draft={draft} setDraft={setDraft} />}
            {section === 'fields' && <AdminFieldsSection draft={draft} setDraft={setDraft} />}
            {section === 'languages' && <AdminLanguagesSection draft={draft} setDraft={setDraft} />}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          px: 2,
          py: 1.25,
          bgcolor: PRES.surface,
          borderTop: `1px solid ${PRES.borderSubtle}`,
          zIndex: 2,
          ...reducedMotionSx,
        }}
      >
        <Button onClick={() => setResetOpen(true)} disabled={busy} sx={{ textTransform: 'none', ...focusVisibleSx }}>
          {COPY.settingsReset}
        </Button>
        <Typography
          aria-live="polite"
          sx={{
            color: sectionDirty ? PRES.warn : PRES.textTertiary,
            fontFamily: TYPE.ui,
            ...TYPE.meta,
            flex: 1,
            minWidth: 140,
          }}
        >
          {sectionDirty ? COPY.settingsDirty : COPY.settingsSavedStatus}
        </Typography>
        {error && <Alert severity="error" sx={{ flex: '1 1 240px', py: 0 }}>{error}</Alert>}
        <Button
          variant="contained"
          disabled={busy || !sectionDirty}
          onClick={() => { void save(); }}
          sx={{
            bgcolor: PRES.interactive,
            textTransform: 'none',
            boxShadow: 'none',
            minHeight: CONTROL.height,
            px: `${SPACE[4]}px`,
            '&:hover': { bgcolor: PRES.interactiveHover },
            ...focusVisibleSx,
          }}
        >
          {settingsSaveLabel(meta.label)}
        </Button>
      </Box>

      <AdminConfirmDialog
        open={resetOpen}
        title={COPY.settingsResetTitle}
        body={COPY.settingsResetBody}
        confirmLabel={COPY.settingsResetConfirm}
        cancelLabel={COPY.cancel}
        onCancel={() => setResetOpen(false)}
        onConfirm={confirmReset}
        danger
      />
      <AdminConfirmDialog
        open={leaveOpen}
        title={COPY.settingsLeaveTitle}
        body={COPY.settingsLeaveBody}
        confirmLabel={COPY.settingsLeaveConfirm}
        cancelLabel={COPY.settingsLeaveStay}
        onCancel={() => setLeaveOpen(false)}
        onConfirm={confirmLeave}
        danger
      />
    </Box>
  );
};

export default PresentationsAdminPage;
